// src/i18n/en/help.ts

export const help = {
  tooltip: 'Help about this page',
  keyFeatures: 'Key Features',
  quickTips: 'Quick Tips',
  
  // Dashboard
  dashboard: {
    title: 'Dashboard',
    description: 'Overview of property activity with real-time metrics, charts and journey analysis.',
    features: {
      kpis: 'Real-time KPIs: Total, complete rate, walking guests and average duration',
      filters: 'Flexible date filters: today, yesterday, 7 days, 30 days or custom range',
      insights: 'Journey Insights: time saved, digital journeys and most used channel',
      charts: 'Interactive charts: channel distribution and daily trends',
    },
    tips: {
      compareData: 'Use date filters to compare periods and detect trends',
      quickFilters: 'Quick Filters take you directly to filtered journeys',
      recentActivity: 'Click on any recent journey to see its full details',
    },
  },
  
  // Activity Monitor
  activityMonitor: {
    title: 'Activity Monitor',
    description: 'Real-time tracking of device status and active sessions in your property.',
    features: {
      realtime: 'Real-time device status: Online, Offline or Idle',
      deviceStatus: 'Last activity and recent events for each device',
      activeSessions: 'Journeys in progress right now with details',
    },
    tips: {
      offline: 'A device Offline for more than 1 hour requires immediate investigation',
      abandoned: 'Abandoned sessions may indicate UX problems on that device',
      autoRefresh: 'Data refreshes automatically every 30 seconds',
    },
  },
  
  // Journeys
  journeys: {
    title: 'Journey History',
    description: 'Search and detailed analysis of all check-ins with advanced filters and visual timeline.',
    features: {
      search: 'Advanced search by reservation number, room or guest',
      filters: 'Combinable filters: date, status, property, walking guests',
      timeline: 'Expandable visual timeline to see the complete flow of each journey',
      export: 'Export to CSV/Excel for external analysis',
    },
    tips: {
      search: 'Use quick search to find specific reservations instantly',
      combine: 'Combine filters for precise analysis (e.g: Walking guests + Last 7 days)',
      timeline: 'Click on the ">" arrow to expand the complete journey timeline',
    },
  },
  
  // Devices
  devices: {
    title: 'Device Management',
    description: 'Manage kiosks, dispensers, tablets and mobile apps connected to the system.',
    features: {
      inventory: 'Complete device inventory with status and last activity',
      configuration: 'Configuration and specific parameters for each device',
      monitoring: 'Status of each device: Active, Inactive or Pending activation, and its last activity',
    },
    tips: {
      naming: 'Use descriptive names (e.g: "Main Reception Kiosk", "Floor 2 Dispenser")',
      location: 'Specify physical location to find devices easily',
      offline: 'Regularly check devices with no recent activity to detect problems',
    },
  },
  
  // Users
  users: {
    title: 'User Management',
    description: 'Manage users, roles, permissions and property access in the system.',
    features: {
      roles: 'Available roles: Group Administrator (their chains), Property Administrator (their properties), Receptionist (Reception only: arrivals, reservations and live board) and Viewer (read-only, their properties)',
      permissions: 'Granular permissions per role with precise access control',
      multiHotel: 'Multi-property assignment: users can manage multiple properties',
    },
    tips: {
      leastPrivilege: 'Principle of least privilege: grant only necessary permissions',
      inactive: 'Deactivate removes Insight access only: the account and its access to other applications do not change, and Activate gives it back as it was',
      superAdmin: 'Use SuperAdmin only for system administrators, not for daily operation',
    },
  },
  
  // Settings
  settings: {
    title: 'Settings',
    description: 'System parameters, integrations, notifications and personal preferences.',
    features: {
      general: 'General settings: timezone, language, date format',
      notifications: 'Configurable notifications via email, SMS or push',
      integrations: 'Integrations with reservation systems, key systems and other services',
    },
    tips: {
      timezone: 'Timezone must match the physical location of the property',
      alerts: 'Configure alerts only for critical events to avoid fatigue',
      save: 'Save changes before leaving each section',
    },
  },
  
  // Alerts
  alerts: {
    title: 'Alert System',
    description: 'The system automatically generates alerts every 10 minutes by checking device status and critical error frequency.',
    features: {
      deviceOffline: 'Device Offline (Warning): triggered when a device has not sent events for 35 minutes (30 min heartbeat interval + 5 min margin). Only fires once per active-to-inactive transition.',
      highErrorRate: 'High Critical Error Rate (Critical): triggered when a device accumulates 10 or more Critical severity errors within 1 hour. 4-hour cooldown between alerts for the same device.',
      dispenserErrors: 'Dispenser Critical Errors (Critical): triggered when a dispenser accumulates 3 or more Critical severity errors within 1 hour. 4-hour cooldown between alerts for the same dispenser.',
    },
    tips: {
      severityVsNature: 'Alerts are based on error Severity (Critical/Warning/Info), not on Nature (Error/Operational/Informational). An operational event with Critical severity (e.g. empty hopper) will trigger an alert.',
      channels: 'Alerts are sent through the channels configured for each property: email, SMS or WhatsApp. Check the property configuration to verify recipients.',
      cooldown: 'After sending an alert, the system waits 4 hours before generating another of the same type for the same device, preventing repetitive alerts.',
      days: "Dates are the property's days: each day runs from the property's day-closing time (04:00 unless it states another) to the same time the next day, in its time zone.",
    },
  },
  
  // Check-ins
  checkIns: {
    title: 'Check-in History',
    description: 'Detailed view of all check-ins performed in the system.',
    features: {
      filters: 'Advanced filters by date, property, status and channel',
      details: 'Complete information for each check-in with event timeline',
      export: 'Export for external analysis and reporting',
    },
    tips: {
      status: '"Complete" check-ins include pre-check-in and key delivery',
      search: 'Search by reservation number to find specific check-ins',
      timeline: 'Expand timeline to see the complete process flow',
    },
  },
  
  // Groups
  groups: {
    title: 'Group Management',
    description: 'Manage property groups and their global configurations.',
    features: {
      hierarchy: 'Organizational hierarchy: Groups > Properties > Devices',
      settings: 'Global configuration inherited by all properties in the group',
      permissions: 'Group-based access control for GroupAdmins',
    },
    tips: {
      naming: 'Use descriptive names that reflect organizational structure',
      inheritance: 'Group configurations are inherited by properties',
      consolidation: 'Group properties by region or brand for better management',
    },
  },
  
  // Hotels
  hotels: {
    title: 'Property Management',
    description: 'Manage individual properties, configurations and user assignments.',
    features: {
      configuration: 'Property-specific configuration: timezone, language, currency',
      devices: 'Management of devices assigned to the property',
      users: 'Users with property access and their permissions',
    },
    tips: {
      active: 'Mark properties as inactive temporarily without losing configuration',
      timezone: 'Property timezone must match its physical location',
      branding: 'Customize colors and logos to maintain brand identity',
    },
  },
  
  // Error Monitor
  errorMonitor: {
    title: 'Error Monitor',
    description: 'Detailed analysis of system errors and abandonments.',
    features: {
      tracking: 'Error tracking by type, device and frequency',
      abandonment: 'Abandonment analysis with critical point identification',
      trends: 'Error trends to identify systematic problems',
    },
    tips: {
      focus: 'Prioritize errors affecting multiple devices',
      context: 'Review complete error context before escalating',
      prevention: 'Use pattern analysis for proactive prevention',
      days: "Dates are the property's days: each day runs from the property's day-closing time (04:00 unless it states another) to the same time the next day, in its time zone.",
    },
  },
};

export default help;
